import { useEffect, useMemo, useState } from 'react'
import type { MeterKind } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import type { DueStatus, MeterStatusRow, ServiceDueRow } from '../sync/transport.ts'

export interface SlotRow { id: string; asset_id: string; kind: MeterKind; unit_code: string }
export interface InstanceRow { id: string; slot_id: string; installed_at: string; baseline_value: number; removed_at?: string | null; replaces_instance_id?: string | null; reason?: string | null }
export type ReviewState = 'pending' | 'accepted' | 'rejected' | null
export interface ObservationRow {
  id: string; meter_instance_id: string; value: number; observed_at: string; entered_at: string; received_at?: string
  source: 'manual' | 'photo' | 'telematics' | 'import' | 'estimated'; estimate_method?: string | null
  quality: 'ok' | 'suspect_regression' | 'suspect_jump'; review_state: ReviewState
  corrects_observation_id?: string | null; correction_reason?: string | null; superseded_by_id?: string | null; is_final: boolean
}
export interface PlanRow { id: string; name: string; asset_id?: string | null; asset_type_code?: string | null }
export interface PlanVersionRow {
  id: string; plan_id: string; version_no: number; trigger_logic: 'OR' | 'AND' | 'CALENDAR_ONLY' | 'METER_ONLY'
  calendar_every?: number | null; calendar_unit?: 'day' | 'week' | 'month' | 'year' | null; meter_slot_kind?: MeterKind | null; meter_interval?: number | null
  source: 'manufacturer' | 'local_approved'; evidence_note: string; valid_from: string
}

/** One meter as a screen shows it. `latest` is the newest reading that has not been superseded or rejected: what somebody last read off
 *  the counter, whatever its verdict. The server alone decides what COUNTS (suspect rows and pending estimates do not): nothing here
 *  re-implements that rule, so the device can never disagree with the due list. */
export interface SlotView {
  slot: SlotRow
  open: InstanceRow | undefined
  instances: InstanceRow[]
  observations: Array<Pending<ObservationRow>>        // newest first, everything incl. superseded (history)
  latest: Pending<ObservationRow> | undefined
  pendingReview: number
}

const byTime = (a: ObservationRow, b: ObservationRow) =>
  a.observed_at < b.observed_at ? 1 : a.observed_at > b.observed_at ? -1 : a.entered_at < b.entered_at ? 1 : a.entered_at > b.entered_at ? -1 : a.id < b.id ? 1 : -1

export function buildSlotViews(assetId: string, slots: SlotRow[], instances: InstanceRow[], observations: Array<Pending<ObservationRow>>): SlotView[] {
  return slots.filter((s) => s.asset_id === assetId).sort((a, b) => a.kind.localeCompare(b.kind)).map((slot) => {
    const mine = instances.filter((i) => i.slot_id === slot.id).sort((a, b) => (a.installed_at < b.installed_at ? 1 : -1))
    const ids = new Set(mine.map((i) => i.id))
    const obs = observations.filter((o) => ids.has(o.meter_instance_id)).sort(byTime)
    const open = mine.find((i) => !i.removed_at)
    const live = obs.filter((o) => !o.superseded_by_id && o.review_state !== 'rejected' && (!open || o.meter_instance_id === open.id))
    return { slot, open, instances: mine, observations: obs, latest: live[0], pendingReview: obs.filter((o) => o.review_state === 'pending' && !o.superseded_by_id).length }
  })
}

export function useMeterData() {
  const slots = useMerged<SlotRow>('meter_slot', 'fleet.meter_slot')
  const instances = useMerged<InstanceRow>('meter_instance', 'fleet.meter_instance')
  const observations = useMerged<ObservationRow>('meter_observation', 'fleet.meter_observation')
  return { slots, instances, observations, ready: !!slots && !!instances && !!observations }
}

export function useSlotViews(assetId: string): SlotView[] | undefined {
  const { slots, instances, observations, ready } = useMeterData()
  return useMemo(() => (ready ? buildSlotViews(assetId, slots!, instances!, observations!) : undefined), [ready, assetId, slots, instances, observations])
}

export const usePlans = () => useMerged<PlanRow>('service_plan', 'fleet.service_plan')
export const usePlanVersions = () => useMerged<PlanVersionRow>('service_plan_version', 'fleet.service_plan_version')

/** Online reads from the server (RLS applies). `reload` re-asks; `online` false means the device could not ask (no error screen: the
 *  local readings still show, the lifetime and due answers simply wait for the network). */
function useServerRead<T>(load: () => Promise<T>, deps: unknown[]): { data: T | null; online: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null)
  const [online, setOnline] = useState(true)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    let live = true
    load().then((d) => { if (live) { setData(d); setOnline(true) } }).catch(() => { if (live) setOnline(false) })
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])
  return { data, online, reload: () => setTick((n) => n + 1) }
}

export function useMeterStatus(assetId: string, signature: string) {
  const { transport } = useClub()
  return useServerRead<MeterStatusRow[]>(() => transport.meterStatus(assetId), [transport, assetId, signature])
}
export function useServiceDue(assetId?: string) {
  const { transport } = useClub()
  return useServerRead<ServiceDueRow[]>(() => transport.serviceDue(assetId), [transport, assetId])
}

export const DUE_ORDER: DueStatus[] = ['overdue', 'due', 'due_soon', 'no_anchor', 'no_meter', 'ok']
export const duePillTone = (s: DueStatus): 'red' | 'amber' | 'gray' | 'green' => (s === 'overdue' || s === 'due' ? 'red' : s === 'due_soon' ? 'amber' : s === 'ok' ? 'green' : 'gray')
